// Nurse-side leave UI (nursing-ops-2 #02), hosted on My Schedule: apply for
// leave (date range + reason) and track my requests. Identity is entirely
// server-side (the backend records the JWT's nurse) — this component never
// knows a nurse id. Withdraw is offered only while a request is pending.
import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Button, Chip, Grid, LinearProgress, Paper, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import { toast } from "react-toastify";

import { NURSE_COLORS } from "../theme";
import { applyLeave, getMyLeaveRequests, actOnLeave, listOf } from "../api/nurseApi";

const STATUS_CHIP = {
  pending: { color: "warning", variant: "filled" },
  approved: { color: "success", variant: "filled" },
  rejected: { color: "error", variant: "outlined" },
  withdrawn: { color: "default", variant: "outlined" },
  revoked: { color: "default", variant: "outlined" },
};

export default function MyLeaveSection({ onChanged }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ from: "", to: "", reason: "" });
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    getMyLeaveRequests()
      .then((r) => setRequests(listOf(r)))
      .catch(() => setRequests([]))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const submit = async () => {
    if (!form.from || !form.to || !form.reason.trim()) return;
    setBusy(true);
    try {
      const res = await applyLeave({ from_date: form.from, to_date: form.to, reason: form.reason.trim() });
      if (res?.status !== "Success") {
        toast.error(res?.message || "Could not submit leave request");
      } else {
        toast.success(res?.message || "Leave request submitted");
        setForm({ from: "", to: "", reason: "" });
        reload();
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not submit leave request");
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async (row) => {
    setBusy(true);
    try {
      const res = await actOnLeave(row.id, "withdraw");
      if (res?.status !== "Success") toast.error(res?.message || "Could not withdraw");
      else { toast.success("Request withdrawn"); reload(); onChanged?.(); }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not withdraw");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>Leave</Typography>
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Apply for leave</Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={6} md={2}>
            <TextField
              type="date" fullWidth size="small" label="From" value={form.from}
              onChange={(e) => setForm({ ...form, from: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              type="date" fullWidth size="small" label="To" value={form.to}
              onChange={(e) => setForm({ ...form, to: e.target.value })}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: form.from || undefined }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth size="small" label="Reason" value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth variant="contained" disabled={busy || !form.from || !form.to || !form.reason.trim()}
              onClick={submit}
            >
              Apply
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 1 }} />}
      <Paper sx={{ borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}`, overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: NURSE_COLORS.pageBg }}>
              <TableCell>From</TableCell><TableCell>To</TableCell><TableCell>Reason</TableCell><TableCell>Status</TableCell><TableCell>Decision</TableCell><TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.map((r) => {
              const chip = STATUS_CHIP[r.status] || STATUS_CHIP.pending;
              return (
                <TableRow key={r.id} hover>
                  <TableCell>{r.from_date}</TableCell>
                  <TableCell>{r.to_date}</TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>
                    <Typography variant="body2" noWrap title={r.reason}>{r.reason}</Typography>
                  </TableCell>
                  <TableCell><Chip size="small" label={r.status} color={chip.color} variant={chip.variant} /></TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {r.decided_by ? `${r.decided_by}${r.decision_note ? ` — ${r.decision_note}` : ""}` : "—"}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {r.status === "pending" && (
                      <Button size="small" color="error" disabled={busy} onClick={() => withdraw(r)}>
                        Withdraw
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {!loading && requests.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>No leave requests yet.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
